import type { HelpTopicDef } from '../types';

export const materials: HelpTopicDef = {
  slug: 'materials',
  title: 'Materials',
  summary: 'A material bills only if you add it by hand. The portal never proposes one.',
  group: 'Daily work',
  updated: '2026-08-24',
  intro: [
    'A foreman’s daily report has a labor table and an equipment table. It has no materials table — at most his notes mention something in passing. So the portal does not try to work materials out from a report: whether a load of rock belongs on a bill is something you know from the job, not something the page says.',
    'The rule is simple: a block carries no materials unless you add one. Most blocks never do, and that is the normal, finished state — a block with nothing in its Materials section is not a block with something left to check.',
  ],
  sections: [
    {
      heading: 'How a material line gets onto a block',
      body: [
        'One way: the "+ Add material" button in the block’s Materials section, in the PDF Inbox, while the block is still pending review. You pick the item from the project’s price list, enter the quantity and the rate, and the line is added.',
        'A hand-added line is final the moment you add it — there is no second confirmation step, because you are both the person adding it and the person approving it. It is tagged "manually added", permanently, and a "Remove" link sits beside it while the block is still pending.',
        'This is the one moment a material gets checked, and it is deliberate: you are here with the vendor paperwork in hand. Nothing downstream asks again. The send window prints every material line on the week under "Hand-added materials" so you see the money before it leaves, but it asks nothing — it has no way to confirm a line the portal never received the paperwork for.',
        'The other way is at the week level: "Add Material" on the week’s detail page adds a line with no source block at all, for something that never appeared on any report. See "Weekly rollups".',
      ],
    },
    {
      heading: 'What the Materials section shows',
      body: [
        'Only lines that bill: the item, the quantity, the rate, and the line total. Hand-added lines carry their "manually added" tag. If the block has none, it says "No materials on this block."',
        'A rate is required when you add a line — enter 0 explicitly for a no-charge line, and the portal asks once more ("Rate is $0 — this line will bill $0.00") so a forgotten rate cannot slide through as a free line.',
        'Materials bill quantity times rate, join the week’s subtotal, and get marked up along with labor and equipment. They are never charged equipment tax and never accrue per diem.',
      ],
    },
    {
      heading: 'Why the portal stopped suggesting materials',
      body: [
        'It used to. The portal read each foreman’s notes, quoted any phrase that looked like a material, and put it on the block as a suggestion you had to either confirm or dismiss — and until you did, the block could not be saved.',
        'It did not work, because there is no pattern in a foreman’s writing to find. "Start placing rock for the area" describes the day’s work; it is not a delivery, and it is not a bill. Suggestions like that arrived constantly, and each one had to be waved off before the block could move. The check cost real time and caught nothing a person did not already know.',
        'So the suggestions are gone, and nothing about a material can hold up a block or a week any more. Save is still a gate — for unrostered people, unrostered equipment, and lines with hours but no rate — but materials are not one of its conditions.',
      ],
    },
  ],
  recipes: [
    {
      task: 'Add a material to a block',
      steps: [
        'Open the PDF in "PDF Inbox" and expand the block. Find the "Materials" section.',
        'Click "+ Add material".',
        'Pick the item from the "Material" dropdown — or "+ New Material" to create a price-list entry on the spot. Its default rate fills in automatically.',
        'Enter the quantity, adjust the rate if this load priced differently, add a reason if it helps the record, and click "Add line".',
      ],
      note: 'The rate you enter is the rate that bills, even if the price list changes later.',
    },
    {
      task: 'Remove a material you added by mistake',
      steps: [
        'While the block is still pending, click "Remove" on the line.',
        'It is gone from the block and bills nothing.',
      ],
      note: 'Once the block is saved the "Remove" link is no longer offered. Re-open the block first, or correct the line on the week’s detail page.',
    },
    {
      task: 'Fix a material’s quantity or rate after the block was saved',
      steps: [
        'Open the week’s detail page and expand the "Materials" section.',
        'While the week is still open, the Qty and Rate cells are editable in place — type the correction and it saves immediately.',
      ],
      note: 'On a week that has already been sent, the cells are locked. Reopen the week first, then re-send — the resend is marked as a revision.',
    },
    {
      task: 'Add a material to a week with no report behind it',
      steps: [
        'Open the week’s detail page and find the "Materials" section header.',
        'Click "Add Material" and fill in the line. A reason is required.',
        'The line is permanently tagged "Manually added" and bills like any other.',
      ],
    },
  ],
  related: [
    { label: 'The PDF Inbox', to: '/help/inbox' },
    { label: 'Weekly rollups', to: '/help/weekly-rollups' },
    { label: 'How the math works', to: '/help/how-the-math-works' },
    { label: 'Project settings', to: '/help/settings' },
  ],
};
